import React, { useEffect, useRef, useState } from "react";
import api, { wsUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, MessageSquare } from "lucide-react";

export default function Chat() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimerRef = useRef(null);

  const loadThreads = async () => {
    const r = await api.get("/chat/threads");
    setThreads(r.data);
    if (r.data.length && !active) setActive(r.data[0]);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadThreads(); }, []);

  const loadMessages = async (tid) => {
    const r = await api.get("/chat/messages", { params: { thread_id: tid } });
    setMessages(r.data);
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 100);
  };

  useEffect(() => {
    if (!active) return;
    loadMessages(active.thread_id);
    // WS
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (e) { console.warn("WS close error", e); }
    }
    const ws = new WebSocket(wsUrl(active.thread_id));
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const p = JSON.parse(ev.data);
        if (p.event === "message") {
          setMessages((m) => [...m, p.data]);
          setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
        } else if (p.event === "typing") {
          if (p.data.from_user_role !== user.role) setOtherTyping(p.data.typing);
        }
      } catch (e) { console.warn("WS message parse error", e); }
    };
    return () => {
      try { ws.close(); } catch (e) { console.warn("WS cleanup close error", e); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.thread_id]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim()) return;
    const body = { text: text.trim(), to_user_id: user.role === "admin" ? active.connector_id : undefined };
    setText("");
    await api.post("/chat/send", body);
  };

  const onType = (e) => {
    setText(e.target.value);
    if (!typing) {
      setTyping(true);
      try { wsRef.current?.send(JSON.stringify({ event: "typing", typing: true })); } catch (e) { console.warn("WS typing send error", e); }
    }
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      setTyping(false);
      try { wsRef.current?.send(JSON.stringify({ event: "typing", typing: false })); } catch (e) { console.warn("WS typing stop send error", e); }
    }, 1200);
  };

  return (
    <div className="h-[calc(100vh-9rem)] flex gap-4" data-testid="chat-page">
      {/* Threads list */}
      <div className="w-72 card flex flex-col overflow-hidden">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <MessageSquare size={16} className="text-primary" />
          <div className="font-heading font-bold">Conversations</div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">No conversations yet</div>}
          {threads.map((t) => (
            <button key={t.thread_id}
              onClick={() => setActive(t)}
              className={`w-full text-left p-3 border-b border-border hover:bg-muted/50 ${active?.thread_id === t.thread_id ? "bg-primary/10" : ""}`}
              data-testid={`thread-${t.connector_username}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm truncate">{user?.role === "admin" ? t.connector_name : "Admin Support"}</div>
                {t.unread > 0 && <span className="badge badge-red">{t.unread}</span>}
              </div>
              <div className="text-xs text-muted-foreground truncate mt-0.5">{t.last_message || "No messages yet"}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 card flex flex-col overflow-hidden">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">Select a conversation</div>
        ) : (
          <>
            <div className="p-4 border-b border-border">
              <div className="font-heading font-bold">{user?.role === "admin" ? active.connector_name : "Admin Support"}</div>
              <div className="text-xs text-muted-foreground">Real-time chat · Secure</div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="messages-container">
              {messages.length === 0 && <div className="text-center text-sm text-muted-foreground py-12">Start the conversation ✨</div>}
              {messages.map((m) => {
                const isMe = m.from_user_id === user.id;
                return (
                  <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div>
                      <div className={`chat-bubble ${isMe ? "me" : "them"}`}>{m.text}</div>
                      <div className={`text-[10px] text-muted-foreground mt-1 ${isMe ? "text-right" : ""}`}>
                        {new Date(m.timestamp).toLocaleString([], { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                        {isMe && (m.read ? <span className="ml-1 text-blue-500">✓✓</span> : <span className="ml-1">✓</span>)}
                      </div>
                    </div>
                  </div>
                );
              })}
              {otherTyping && (
                <div className="flex justify-start">
                  <div className="chat-bubble them">
                    <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                  </div>
                </div>
              )}
            </div>
            <form onSubmit={send} className="border-t border-border p-3 flex gap-2" data-testid="send-form">
              <input value={text} onChange={onType} placeholder="Type a message…" className="input flex-1" data-testid="chat-input" />
              <button type="submit" className="btn btn-primary" data-testid="btn-send-message"><Send size={16} /></button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
